"use strict";

/*==============================================================================*
*TRESTLE™ W004
*TRESTLE Research Hub
*Version 1.2
*
*Recommendation Architecture
*
*W002 Profile:
*1. My Promise
*2. My Life Stage
*3. My Diabetes Profile
*
*Matching Rule:
*Promise OR Life Stage OR Diabetes Profile
*
*Ranking:
*1. More specific Life Stage matches receive priority
*2. Higher total profile match score receives priority
*3. Maximum 5 resources appear under Recommended for You
*4. All remaining active resources appear under Explore More
*==============================================================================*/

const W004={


/*==============================================================================*
*STATE
*==============================================================================*/

state:{

    recommended:[],

    explore:[],

    recommendedIndex:0,

    exploreIndex:0,

    recommendationLimit:5

},


/*==============================================================================*
*DOM
*==============================================================================*/

ui:{

    recommendedTrack:null,

    exploreTrack:null,

    recommendedPrev:null,

    recommendedNext:null,

    explorePrev:null,

    exploreNext:null,

    modal:null,

    modalTitle:null,

    modalDescription:null,

    modalTool:null,

    modalLearnMore:null,

    modalClose:null

},


/*==============================================================================*
*INIT
*==============================================================================*/

init(){

    this.cacheDOM();

    this.bindEvents();

    this.reset();

    console.log(

        "W004 Ready"

    );

},


/*==============================================================================*
*CACHE DOM
*==============================================================================*/

cacheDOM(){

    this.ui.recommendedTrack=

        document.getElementById(

            "trW004RecommendedTrack"

        );


    this.ui.exploreTrack=

        document.getElementById(

            "trW004ExploreTrack"

        );


    this.ui.recommendedPrev=

        document.getElementById(

            "trW004RecommendedPrev"

        );


    this.ui.recommendedNext=

        document.getElementById(

            "trW004RecommendedNext"

        );


    this.ui.explorePrev=

        document.getElementById(

            "trW004ExplorePrev"

        );


    this.ui.exploreNext=

        document.getElementById(

            "trW004ExploreNext"

        );


    this.ui.modal=

        document.getElementById(

            "trW004Modal"

        );


    this.ui.modalTitle=

        document.getElementById(

            "trW004ModalTitle"

        );


    this.ui.modalDescription=

        document.getElementById(

            "trW004ModalDescription"

        );


    this.ui.modalTool=

        document.getElementById(

            "trW004ModalTool"

        );


    this.ui.modalLearnMore=

        document.getElementById(

            "trW004LearnMore"

        );


    this.ui.modalClose=

        document.getElementById(

            "trW004ModalClose"

        );

},


/*==============================================================================*
*LOAD PROFILE
*==============================================================================*/

loadProfile(){

    const profile=

        localStorage.getItem(

            "trestleProfile"

        );


    if(!profile){

        return null;

    }


    try{

        return JSON.parse(

            profile

        );

    }


    catch(error){

        console.error(

            "W004 Profile Error",

            error

        );


        return null;

    }

},


/*==============================================================================*
*SCORE RESOURCE
*
*Each W002 profile dimension may match independently.
*
*Promise       = +1
*Life Stage    = +1
*Diabetes Type = +1
*
*This preserves the OR architecture.
*==============================================================================*/

scoreResource(resource,profile){

    let score=0;


    const matches={

        promise:false,

        lifeStage:false,

        diabetesType:false

    };


    if(

        resource.relevance.promise.includes(

            profile.promise

        )

    ){

        score++;

        matches.promise=true;

    }


    if(

        resource.relevance.lifeStage.includes(

            profile.lifeStage

        )

    ){

        score++;

        matches.lifeStage=true;

    }


    if(

        resource.relevance.diabetesType.includes(

            profile.diabetesType

        )

    ){

        score++;

        matches.diabetesType=true;

    }


    return{

        score:score,

        matches:matches

    };

},


/*==============================================================================*
*LIFE STAGE SPECIFICITY
*
*A tool mapped to fewer Life Stages is considered more specifically relevant
*when the selected Life Stage matches.
*
*Example:
*
*Perimenopause tool:
*Postpartum / Perimenopause / Menopause
*
*is more specific than a universal tool mapped to all six Life Stages.
*==============================================================================*/

getLifeStageSpecificity(resource,matches){

    if(

        !matches.lifeStage

    ){

        return -1;

    }


    const lifeStages=

        resource.relevance.lifeStage || [];


    const totalLifeStages=6;


    return Math.max(

        0,

        totalLifeStages-lifeStages.length

    );

},


/*==============================================================================*
*BUILD RECOMMENDATIONS
*
*STEP 1
*Score all active resources.
*
*STEP 2
*Any score above 0 is an OR match.
*
*STEP 3
*Rank matched resources:
*
* - specific Life Stage relevance first
* - then higher total match score
*
*STEP 4
*Top 5 = Recommended for You.
*
*STEP 5
*Everything else = Explore More.
*==============================================================================*/

buildRecommendations(){

    const profile=

        this.loadProfile();


    const activeResources=

        TRESTLE_W004_RESEARCH_DATA.filter(

            (resource)=>

                resource.active===true

        );


    /*==========================================================================
    NO TRESTLE PROFILE
    ==========================================================================*/

    if(!profile){

        this.state.recommended=[];

        this.state.explore=

            activeResources;

        return;

    }


    /*==========================================================================
    SCORE EVERY ACTIVE RESOURCE
    ==========================================================================*/

    const scored=

        activeResources.map(

            (resource)=>{


                const result=

                    this.scoreResource(

                        resource,

                        profile

                    );


                const lifeStageSpecificity=

                    this.getLifeStageSpecificity(

                        resource,

                        result.matches

                    );


                return{

                    resource:resource,

                    score:result.score,

                    matches:result.matches,

                    lifeStageSpecificity:
                        lifeStageSpecificity

                };

            }

        );


    /*==========================================================================
    MATCHED RESOURCES
    TRUE OR LOGIC
    ==========================================================================*/

    const matched=

        scored.filter(

            (item)=>

                item.score>0

        );


    /*==========================================================================
    RANK MATCHED RESOURCES
    ==========================================================================*/

    matched.sort(

        (a,b)=>{


            /*------------------------------------------------------------------
            PRIORITY 1
            More specific Life Stage match.
            ------------------------------------------------------------------*/

            if(

                b.lifeStageSpecificity !==
                a.lifeStageSpecificity

            ){

                return(

                    b.lifeStageSpecificity -
                    a.lifeStageSpecificity

                );

            }


            /*------------------------------------------------------------------
            PRIORITY 2
            More W002 dimensions matched.
            ------------------------------------------------------------------*/

            if(

                b.score !==
                a.score

            ){

                return(

                    b.score -
                    a.score

                );

            }


            /*------------------------------------------------------------------
            PRIORITY 3
            Preserve original Data.js order.
            ------------------------------------------------------------------*/

            return 0;

        }

    );


    /*==========================================================================
    RECOMMENDED FOR YOU
    Maximum 5
    ==========================================================================*/

    const recommendedScored=

        matched.slice(

            0,

            this.state.recommendationLimit

        );


    this.state.recommended=

        recommendedScored.map(

            (item)=>({

                ...item.resource,

                matchScore:
                    item.score,

                matches:
                    item.matches

            })

        );


    /*==========================================================================
    BUILD SET OF RECOMMENDED IDS
    ==========================================================================*/

    const recommendedIds=

        new Set(

            this.state.recommended.map(

                (resource)=>

                    resource.id

            )

        );


    /*==========================================================================
    EXPLORE MORE
*
*Includes:
* - matched resources that did not enter Top 5
* - completely unmatched resources
*==========================================================================*/

    this.state.explore=

        activeResources.filter(

            (resource)=>

                !recommendedIds.has(

                    resource.id

                )

        );

},


/*==============================================================================*
*RESET
*==============================================================================*/

reset(){

    this.state.recommendedIndex=0;

    this.state.exploreIndex=0;


    this.buildRecommendations();


    this.render();

},


/*==============================================================================*
*RENDER
*==============================================================================*/

render(){

    this.renderRecommended();

    this.renderExplore();

},


/*==============================================================================*
*RENDER RECOMMENDED
*==============================================================================*/

renderRecommended(){

    if(

        !this.ui.recommendedTrack

    ){

        return;

    }


    if(

        this.state.recommended.length===0

    ){

        this.ui.recommendedTrack.innerHTML=`

            <div class="tr-w004-empty">

                Currently we do not have a matching research tool for your TRESTLE Profile.

            </div>

        `;


        return;

    }


    this.ui.recommendedTrack.innerHTML=

        this.state.recommended

            .map(

                (resource)=>

                    this.createResourceCard(

                        resource

                    )

            )

            .join("");

},


/*==============================================================================*
*RENDER EXPLORE
*==============================================================================*/

renderExplore(){

    if(

        !this.ui.exploreTrack

    ){

        return;

    }


    if(

        this.state.explore.length===0

    ){

        this.ui.exploreTrack.innerHTML=`

            <div class="tr-w004-empty">

                No additional research tools are currently available.

            </div>

        `;


        return;

    }


    this.ui.exploreTrack.innerHTML=

        this.state.explore

            .map(

                (resource)=>

                    this.createResourceCard(

                        resource

                    )

            )

            .join("");

},
/*==============================================================================*
*CREATE RESOURCE CARD
*==============================================================================*/

createResourceCard(resource){

    if(!resource.image){

        return "";

    }


    return`

    <button
    type="button"
    class="tr-w004-resource"
    data-resource-id="${resource.id}"
    aria-label="Open ${resource.title}">


        <div class="tr-w004-resource-image">


            <img
            src="${resource.image}"
            alt="${resource.title}">


        </div>


    </button>

    `;

},


/*==============================================================================*
*BIND EVENTS
*==============================================================================*/

bindEvents(){

    document.addEventListener(

        "click",

        (event)=>{


            const card=

                event.target.closest(

                    ".tr-w004-resource"

                );


            if(card){

                this.openResource(

                    card.dataset.resourceId

                );


                return;

            }


            if(

                event.target.hasAttribute(

                    "data-w004-close"

                )

            ){

                this.closeModal();

            }

        }

    );


    if(this.ui.recommendedPrev){

        this.ui.recommendedPrev.addEventListener(

            "click",

            ()=>{

                this.scrollCarousel(

                    "recommended",

                    -1

                );

            }

        );

    }


    if(this.ui.recommendedNext){

        this.ui.recommendedNext.addEventListener(

            "click",

            ()=>{

                this.scrollCarousel(

                    "recommended",

                    1

                );

            }

        );

    }


    if(this.ui.explorePrev){

        this.ui.explorePrev.addEventListener(

            "click",

            ()=>{

                this.scrollCarousel(

                    "explore",

                    -1

                );

            }

        );

    }


    if(this.ui.exploreNext){

        this.ui.exploreNext.addEventListener(

            "click",

            ()=>{

                this.scrollCarousel(

                    "explore",

                    1

                );

            }

        );

    }


    if(this.ui.modalClose){

        this.ui.modalClose.addEventListener(

            "click",

            ()=>{

                this.closeModal();

            }

        );

    }


    document.addEventListener(

        "keydown",

        (event)=>{

            if(

                event.key==="Escape" &&
                this.ui.modal &&
                !this.ui.modal.hidden

            ){

                this.closeModal();

            }

        }

    );

},


/*==============================================================================*
*SCROLL CAROUSEL
*==============================================================================*/

scrollCarousel(type,direction){

    let carousel=null;


    if(type==="recommended"){

        carousel=

            document.getElementById(

                "trW004RecommendedCarousel"

            );

    }


    if(type==="explore"){

        carousel=

            document.getElementById(

                "trW004ExploreCarousel"

            );

    }


    if(!carousel){

        return;

    }


    const card=

        carousel.querySelector(

            ".tr-w004-resource"

        );


    if(!card){

        return;

    }


    const cardWidth=

        card.getBoundingClientRect().width;


    const gap=18;


    const move=

        (cardWidth+gap)*3;


    carousel.scrollBy({

        left:direction*move,

        behavior:"smooth"

    });

},


/*==============================================================================*
*OPEN RESOURCE
*==============================================================================*/

openResource(resourceId){

    const resource=

        TRESTLE_W004_RESEARCH_DATA.find(

            (item)=>

                item.id===resourceId

        );


    if(!resource){

        return;

    }


    this.ui.modalTitle.textContent=

        resource.title;


    this.ui.modalDescription.textContent=

        resource.description||"";


    this.ui.modalLearnMore.href=

        resource.learnMore||"#";


    this.renderEmbeddedTool(

        resource

    );


    this.ui.modal.hidden=false;


    document.body.style.overflow=

        "hidden";

},


/*==============================================================================*
*RENDER EMBEDDED TOOL
*==============================================================================*/

renderEmbeddedTool(resource){

    if(

        !this.ui.modalTool ||
        !resource ||
        !resource.embedId

    ){

        return;

    }


    const registry=

        window.TRESTLE_W004_TOOLS || {};


    const tool=

        registry[

            resource.embedId

        ];


    if(!tool){

        this.ui.modalTool.innerHTML=`

            <div class="tr-w004-empty">

                This research tool is currently unavailable inside TRESTLE.

            </div>

        `;


        return;

    }


    if(

        typeof tool.render!=="function"

    ){

        return;

    }


    this.ui.modalTool.innerHTML=

        tool.render();


    if(

        typeof tool.init==="function"

    ){

        tool.init(

            this.ui.modalTool

        );

    }

},


/*==============================================================================*
*CLOSE MODAL
*==============================================================================*/

closeModal(){

    if(!this.ui.modal){

        return;

    }


    this.ui.modal.hidden=true;


    if(this.ui.modalTool){

        this.ui.modalTool.innerHTML="";

    }


    document.body.style.overflow="";

}

};


/*==============================================================================*
*W002 PROFILE UPDATE LISTENER
*
*Whenever W002 saves a revised TRESTLE Profile,
*W004 recalculates and re-ranks the recommendations.
*==============================================================================*/

window.addEventListener(

    "trestle-profile-updated",

    ()=>{

        W004.reset();


        console.log(

            "W004 Updated From TRESTLE Profile"

        );

    }

);


/*==============================================================================*
*BOOTSTRAP
*==============================================================================*/

document.addEventListener(

    "DOMContentLoaded",

    ()=>{

        W004.init();

    }

);